import { z } from 'zod';
// What Dokless tells Plancia about what it did with a payload (Dokless's rules
// v1, docs/dokless/regole-dokless-v1.md §10; ADR 0084):
// - in the answer to `POST /trackings/{id}/events`: `accepted` (`all|partial|none`)
//   and `rejected[]` (§10.1), or `200 {"ignored": "stale_revision"}` (§2);
// - later, through Plancia's `POST /dokless/v2/trackings/{dokless_id}/feedback` (§10.2).
// The answer is read leniently: Dokless's 201 of today has neither key and is
// still a delivery; a malformed item is kept, shortened, never a reason to retry.
/** What Dokless says it applied (§10.1). */
export const DOKLESS_ACCEPTED = ['all', 'partial', 'none'];
/**
 * How Plancia files a 2xx answer: Dokless's `accepted`, the replay it ignored
 * (`stale_revision`), or `unspecified` (today's 201: no `accepted`, no `rejected`).
 */
export const DOKLESS_ANSWER_OUTCOMES = [...DOKLESS_ACCEPTED, 'stale_revision', 'unspecified'];
/** Answers that carried at least one discard (§9's «payload con scarti»). */
export const hasDiscards = (outcome, rejected) => outcome === 'partial' || outcome === 'none' || rejected > 0;
/** One `rejected[]` item as §10.1 writes it (documentation and examples; the deliverer reads leniently). */
export const doklessRejectionWireSchema = z.looseObject({
    rule: z.string(),
    field: z.string().nullable().optional(),
    severity: z.string().nullable().optional(),
    value_summary: z.string().nullable().optional(),
    kept: z.string().nullable().optional(),
    message: z.object({ it: z.string(), en: z.string() }).nullable().optional(),
});
/** At most this many discards are kept per answer. */
export const MAX_REJECTIONS = 200;
const text = (value, max) => {
    if (typeof value === 'string')
        return value.trim() === '' ? null : value.slice(0, max);
    if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);
    if (value === null || value === undefined)
        return null;
    return JSON.stringify(value).slice(0, max);
};
function rejectionOf(item) {
    if (!item || typeof item !== 'object' || Array.isArray(item))
        return {
            rule: 'unreadable',
            field: null,
            severity: null,
            value_summary: text(item, 500),
            kept: null,
            message: null,
        };
    const r = item;
    const message = r.message;
    const it = text(message?.it, 1000);
    const en = text(message?.en, 1000);
    return {
        rule: text(r.rule, 64) ?? 'unknown',
        field: text(r.field, 255),
        severity: text(r.severity, 16),
        value_summary: text(r.value_summary, 1000),
        kept: text(r.kept, 500),
        message: it || en ? { it: it ?? en ?? '', en: en ?? it ?? '' } : null,
    };
}
/**
 * Dokless's 2xx answer to a payload, read as §10.1 says and as today's 201 is:
 * `{"ignored":"stale_revision"}` → `stale_revision`; `accepted` → itself;
 * no `accepted` but discards → `partial`; nothing → `unspecified`.
 */
export function parseDoklessAnswer(body) {
    const b = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
    const ignored = text(b.ignored, 64);
    const accepted = DOKLESS_ACCEPTED.includes(b.accepted)
        ? b.accepted
        : null;
    const rejected = Array.isArray(b.rejected) ? b.rejected.slice(0, MAX_REJECTIONS).map(rejectionOf) : [];
    const outcome = ignored === 'stale_revision'
        ? 'stale_revision'
        : (accepted ?? (rejected.length > 0 ? 'partial' : 'unspecified'));
    return { outcome, accepted, rejected, ignored };
}
// ── §10.2 · the feedback endpoint ──────────────────────────────────────────
export const FEEDBACK_SEVERITIES = ['error', 'warning', 'info'];
export const FEEDBACK_RESOLUTIONS = ['applied_by_backoffice', 'ignored_by_backoffice'];
/**
 * `POST /dokless/v2/trackings/{dokless_id}/feedback` (§10.2): a discard Dokless
 * found after the ingest (a stale tracking, an expired planned event, a
 * suspended withdrawal a person decided). Idempotent on (dokless_id, rule, revision).
 */
export const doklessFeedbackSchema = z.object({
    /** A rule code of §11 (or a new one: rules are open, like alert codes). */
    rule: z.string().regex(/^[a-z][a-z0-9_]{1,47}$/),
    severity: z.enum(FEEDBACK_SEVERITIES),
    /**
     * The payload revision the discard is about. `null` (a tracking that has had no v2 payload yet,
     * Dokless's `getPlanciaRevision()`) is revision 0: the key (dokless_id, rule, 0) stays one row
     * (owner, 19/10/2026; ADR 0084 note).
     */
    revision: z
        .int()
        .min(0)
        .nullable()
        .transform((revision) => revision ?? 0),
    detected_at: z.iso.datetime({ offset: true }),
    field: z.string().max(128).nullable().optional(),
    message: z.object({ it: z.string().min(1).max(1000), en: z.string().min(1).max(1000) }),
    resolution: z.enum(FEEDBACK_RESOLUTIONS).nullable().optional(),
});
//# sourceMappingURL=answer.schema.js.map